import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { SectionScaffold } from '../../components/SectionScaffold/SectionScaffold'
import { ProtectedImage } from '../../components/ProtectedImage/ProtectedImage'
import { content } from '../../data/content'
import { useWechatMediaMode } from '../../hooks/useWechatMediaMode'
import {
  createMotionMedia,
  gsap,
  initializeWhenNear,
  motionConditions,
  motionTokens,
  type MotionConditionState,
} from '../../motion'
import styles from './ClosingSection.module.css'

const finaleVideo =
  '/assets/videos/jimeng-2026-08-13-3963-生成白天到夜晚的延时效果，过渡丝滑自然.mp4'
const finaleVideoMobile = '/assets/optimized/videos/finale-mobile-1080.mp4'
const finalePoster = '/assets/optimized/posters/finale-1920.webp'
const finaleWechatAnimation = '/assets/animations/sec-006-wechat.webp'

export function ClosingSection() {
  const sectionRef = useRef<HTMLDivElement>(null)
  const textRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false)
  const [isVideoPlaying, setIsVideoPlaying] = useState(false)
  const [isWechatAnimationReady, setIsWechatAnimationReady] = useState(false)
  const { isWechat, reduceMotion } = useWechatMediaMode()
  const copy = content['SEC-006']
  const [titleLineOne, titleLineTwo] = copy.title.split('，')

  const bindVideo = useCallback((video: HTMLVideoElement | null) => {
    videoRef.current = video

    if (!video) return

    video.autoplay = true
    video.defaultMuted = true
    video.muted = true
    video.loop = true
    video.playsInline = true
    video.setAttribute('autoplay', '')
    video.setAttribute('muted', '')
    video.setAttribute('loop', '')
    video.setAttribute('playsinline', '')
    video.setAttribute('webkit-playsinline', '')
  }, [])

  useEffect(() => {
    const section = sectionRef.current
    if (!section) return

    if (!('IntersectionObserver' in window)) {
      const timer = setTimeout(() => setShouldLoadVideo(true), 0)
      return () => clearTimeout(timer)
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return
        setShouldLoadVideo(true)
        observer.disconnect()
      },
      { rootMargin: '100% 0px', threshold: 0.01 },
    )

    observer.observe(section)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (!shouldLoadVideo) return
    const video = videoRef.current
    if (!video) return

    let disposed = false

    const playVideo = () => {
      if (disposed || !video.paused) return
      video.defaultMuted = true
      video.muted = true
      void video.play().catch(() => undefined)
    }

    const markPlaying = () => {
      if (!disposed) setIsVideoPlaying(true)
    }

    const showPosterFallback = () => {
      if (!disposed) setIsVideoPlaying(false)
    }

    const resumeVisibleVideo = () => {
      if (document.visibilityState === 'visible') playVideo()
    }

    video.addEventListener('canplay', playVideo)
    video.addEventListener('loadeddata', playVideo)
    video.addEventListener('playing', markPlaying)
    video.addEventListener('pause', showPosterFallback)
    video.addEventListener('waiting', showPosterFallback)
    video.addEventListener('stalled', showPosterFallback)
    video.addEventListener('error', showPosterFallback)
    window.addEventListener('pageshow', playVideo)
    document.addEventListener('visibilitychange', resumeVisibleVideo)
    document.addEventListener('touchstart', playVideo, { passive: true })
    document.addEventListener('pointerdown', playVideo, { passive: true })
    document.addEventListener('WeixinJSBridgeReady', playVideo)
    video.load()
    playVideo()

    return () => {
      disposed = true
      video.removeEventListener('canplay', playVideo)
      video.removeEventListener('loadeddata', playVideo)
      video.removeEventListener('playing', markPlaying)
      video.removeEventListener('pause', showPosterFallback)
      video.removeEventListener('waiting', showPosterFallback)
      video.removeEventListener('stalled', showPosterFallback)
      video.removeEventListener('error', showPosterFallback)
      window.removeEventListener('pageshow', playVideo)
      document.removeEventListener('visibilitychange', resumeVisibleVideo)
      document.removeEventListener('touchstart', playVideo)
      document.removeEventListener('pointerdown', playVideo)
      document.removeEventListener('WeixinJSBridgeReady', playVideo)
    }
  }, [shouldLoadVideo])

  useLayoutEffect(() => {
    const section = sectionRef.current
    const text = textRef.current
    if (!section || !text) return

    return initializeWhenNear(section, () => {
      const media = createMotionMedia()

      media.add(motionConditions, (context) => {
        const { reduceMotion } = context.conditions as MotionConditionState

        if (reduceMotion) {
          gsap.set(text, { autoAlpha: 1, y: 0 })
          return
        }

        gsap.fromTo(
          text,
          { autoAlpha: 0, y: motionTokens.distance.small },
          {
            autoAlpha: 1,
            y: 0,
            duration: motionTokens.duration.slow,
            ease: motionTokens.ease.entrance,
            scrollTrigger: {
              trigger: section,
              start: 'top 80%',
              once: true,
            },
          },
        )
      })

      return () => media.revert()
    })
  }, [])

  return (
    <SectionScaffold
      id="SEC-006"
      className={styles.section}
      labelledBy="sec-006-title"
    >
      <div ref={sectionRef} className={styles.viewport}>
        <ProtectedImage
          className={styles.backgroundPoster}
          src={finalePoster}
          alt=""
          loading="lazy"
          decoding="async"
          aria-hidden="true"
          draggable={false}
        />
        {isWechat ? (
          shouldLoadVideo && !reduceMotion ? (
            <ProtectedImage
              className={`${styles.background} ${
                isWechatAnimationReady ? styles.backgroundReady : ''
              }`}
              src={finaleWechatAnimation}
              alt=""
              decoding="async"
              aria-hidden="true"
              draggable={false}
              onLoad={() => setIsWechatAnimationReady(true)}
              onError={() => setIsWechatAnimationReady(false)}
            />
          ) : null
        ) : (
          <video
            ref={bindVideo}
            className={`${styles.background} ${
              isVideoPlaying ? styles.backgroundReady : ''
            }`}
            autoPlay
            muted
            loop
            playsInline
            preload="none"
            aria-hidden="true"
            tabIndex={-1}
            draggable={false}
          >
            {shouldLoadVideo ? (
              <>
                <source
                  media="(max-width: 47.9375rem)"
                  src={finaleVideoMobile}
                  type="video/mp4"
                />
                <source src={finaleVideo} type="video/mp4" />
              </>
            ) : null}
          </video>
        )}
        <div className={styles.overlay} aria-hidden="true" />
        <div ref={textRef} className={styles.content}>
          <h2 id="sec-006-title" className={styles.title}>
            <span className={styles.titleDesktop}>{copy.title}</span>
            <span className={styles.titleMobile}>
              <span>{titleLineOne}</span>
              <span>{titleLineTwo}</span>
            </span>
          </h2>
          <p className={styles.body}>{copy.body}</p>
        </div>
      </div>
    </SectionScaffold>
  )
}
